import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
// Footer navigation mirrors the header: Platform · Industries · Case Study · Trust · Company
const COLUMN_TITLES_FR: Record<string, string> = {
  Platform: 'Plateforme',
  Industries: 'Secteurs',
  'Case Study': 'Étude de cas',
  Trust: 'Confiance',
  Company: 'Entreprise'
};
const LINK_LABELS_FR: Record<string, string> = {
  Identity: 'Identité',
  'Digital Card': 'Carte numérique',
  Communications: 'Communications',
  Requests: 'Requêtes',
  Collect: 'Collecte',
  'Smart Water': 'Compteurs intelligents',
  Analytics: 'Analytique',
  'ATNA Intelligence': 'Intelligence ATNA',
  'Cities & Government': 'Villes & Gouvernement',
  'Airports & Transportation': 'Aéroports & Transport',
  'Telecommunications & Media': 'Télécom & Médias',
  'Health Networks': 'Réseaux de Santé',
  'Universities & Campuses': 'Universités & Campus',
  'City of Brossard': 'Ville de Brossard',
  'Security & Compliance': 'Sécurité et conformité',
  'About BCITI': 'À propos de BCITI',
  Contact: 'Contact'
};
const linkLabel = (label: string, lang: string) => lang === 'FR' ? LINK_LABELS_FR[label] ?? label : label;
// Remonte la page tout en haut lorsqu'on suit un lien du footer.
const scrollTop = () => window.scrollTo({
  top: 0,
  left: 0,
  behavior: 'auto'
});
const NAV_COLUMNS = [{
  title: 'Platform',
  links: [{
    label: 'Identity',
    href: '/core-infrastructure'
  }, {
    label: 'Digital Card',
    href: '/digital-card'
  }, {
    label: 'Communications',
    href: '/communications'
  }, {
    label: 'Requests',
    href: '/requests'
  }, {
    label: 'Collect',
    href: '/collect'
  }, {
    label: 'Smart Water',
    href: '/smart-water'
  }, {
    label: 'Analytics',
    href: '/analytics'
  }, {
    label: 'ATNA Intelligence',
    href: '/atna'
  }]
}, {
  title: 'Industries',
  links: [{
    label: 'Cities & Government',
    href: '/industries/cities-government'
  }, {
    label: 'Airports & Transportation',
    href: '/industries/airports'
  }, {
    label: 'Telecommunications & Media',
    href: '/industries/telecom'
  }, {
    label: 'Health Networks',
    href: '/industries/health'
  }, {
    label: 'Universities & Campuses',
    href: '/industries/universities'
  }]
}, {
  title: 'Case Study',
  links: [{
    label: 'City of Brossard',
    href: '/brossard',
    featured: true
  }]
}, {
  title: 'Trust',
  links: [{
    label: 'Security & Compliance',
    href: '/trust'
  }]
}, {
  title: 'Company',
  links: [{
    label: 'About BCITI',
    href: '/about'
  }, {
    label: 'Contact',
    href: '/request-demo'
  }]
}];
function MobileNavSection({
  column
}: {
  column: (typeof NAV_COLUMNS)[0];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    lang
  } = useLanguage();
  const title = lang === 'FR' ? COLUMN_TITLES_FR[column.title] ?? column.title : column.title;
  return <div className="border-b border-[#E4E6EC] last:border-none" data-id="element-266">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-4 flex items-center justify-between text-left" data-id="element-267">
        <span className="font-serif text-lg text-black" data-id="element-268">{title}</span>
        <ChevronDownIcon className={`w-4 h-4 text-black/40 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-269" />
      </button>
      <AnimatePresence initial={false} data-id="element-270">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-271">
            <ul className="pb-6 space-y-3" data-id="element-272">
              {column.links.map(link => <li key={link.label} data-id="element-273">
                  {link.href.startsWith('/') ? <Link to={link.href} onClick={scrollTop} className={`font-sans text-sm transition-colors duration-200 hover:text-indigo ${link.featured ? 'text-black font-medium' : 'text-black/60'}`} data-id="element-274">
                      {linkLabel(link.label, lang)}
                    </Link> : <a href={link.href} onClick={scrollTop} className={`font-sans text-sm transition-colors duration-200 hover:text-indigo ${link.featured ? 'text-black font-medium' : 'text-black/60'}`} data-id="element-275">
                      {linkLabel(link.label, lang)}
                    </a>}
                </li>)}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Footer() {
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  return <footer className="bg-white pt-16 md:pt-24 pb-8 px-6" data-id="element-276">
      <div className="max-w-[1400px] mx-auto" data-id="element-277">
        {/* SECTION 1: TOP FOOTER LOCKUP */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8 pb-12 border-b border-[#E4E6EC]" data-id="element-278">
          <div data-id="element-279">
            <img src="https://cdn.magicpatterns.com/uploads/qor6YZgiib6GxTNju4EDoy/Logo_BCITI_(5).png" alt="BCITI" className="h-[3.25rem] w-auto object-contain" data-id="element-280" />
            <p className="mt-5 font-serif text-base text-black/60 leading-relaxed max-w-sm" data-id="element-281">
              {t('The Trusted Intelligence Infrastructure for Communities.', "L'infrastructure d'intelligence de confiance pour les collectivités.")}
            </p>
          </div>
          <Link to="/request-demo" onClick={scrollTop} className="inline-flex items-center gap-2 px-6 py-3 bg-indigo text-white font-sans text-sm font-medium rounded-none hover:bg-indigo/90 transition-colors duration-200" data-id="element-282">
            {t('Request a Demo', 'Demander une démo')}
            <ArrowRightIcon className="w-4 h-4" data-id="element-283" />
          </Link>
        </div>

        {/* SECTION 2: FOOTER NAVIGATION GRID — mirrors header (Platform · Industries · Case Study · Trust · Company) */}
        {/* Desktop & Tablet */}
        <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-10 py-16" data-id="element-284">
          {NAV_COLUMNS.map(col => <div key={col.title} data-id="element-285">
              <h3 className="font-serif text-lg text-black mb-6" data-id="element-286">
                {lang === 'FR' ? COLUMN_TITLES_FR[col.title] ?? col.title : col.title}
              </h3>
              <ul className="space-y-4" data-id="element-287">
                {col.links.map(link => <li key={link.label} data-id="element-288">
                    {link.href.startsWith('/') ? <Link to={link.href} onClick={scrollTop} className={`font-sans text-sm transition-colors duration-200 hover:text-indigo ${link.featured ? 'text-black font-medium' : 'text-black/60'}`} data-id="element-289">
                        {linkLabel(link.label, lang)}
                      </Link> : <a href={link.href} onClick={scrollTop} className={`font-sans text-sm transition-colors duration-200 hover:text-indigo ${link.featured ? 'text-black font-medium' : 'text-black/60'}`} data-id="element-290">
                        {linkLabel(link.label, lang)}
                      </a>}
                  </li>)}
              </ul>
            </div>)}
        </div>

        {/* Mobile Accordion */}
        <div className="md:hidden py-8" data-id="element-291">
          {NAV_COLUMNS.map(col => <MobileNavSection key={col.title} column={col} data-id="element-292" />)}
        </div>

        {/* SECTION 3: TRUST BAR */}
        <div className="py-6 border-y border-[#E4E6EC] text-center" data-id="element-293">
          <span className="font-sans text-xs tracking-[0.15em] uppercase text-black/50" data-id="element-294">
            {t('Law 25', 'Loi 25')}{' '}
            <span className="mx-3 text-[#E4E6EC]" data-id="element-295">|</span>{' '}
            {t('Sovereign AI', 'IA souveraine')}{' '}
            <span className="mx-3 text-[#E4E6EC]" data-id="element-296">|</span>{' '}
            {t('Canadian Hosted Data', 'Données hébergées au Canada')}
          </span>
        </div>

        {/* SECTION 4: BOTTOM LEGAL BAR */}
        <div className="pt-8 flex flex-col lg:flex-row justify-between items-center gap-6 font-sans text-xs text-black/40" data-id="element-297">
          <div data-id="element-298">© 2026 BCITI Inc.</div>

          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2" data-id="element-299">
            <a href="#" className="hover:text-indigo transition-colors duration-200" data-id="element-300">
              {t('Privacy', 'Confidentialité')}
            </a>
            <span className="text-[#E4E6EC]" data-id="element-301">•</span>
            <a href="#" className="hover:text-indigo transition-colors duration-200" data-id="element-302">
              {t('Terms', 'Conditions')}
            </a>
            <span className="text-[#E4E6EC]" data-id="element-303">•</span>
            <a href="#" className="hover:text-indigo transition-colors duration-200" data-id="element-304">
              {t('Security', 'Sécurité')}
            </a>
          </div>

          <div className="flex items-center gap-6" data-id="element-305">
            <a href="#" className="hover:text-indigo transition-colors duration-200" data-id="element-306">
              LinkedIn
            </a>
            <span className="text-[#E4E6EC]" data-id="element-307">•</span>
            <div className="flex items-center gap-2" data-id="element-308">
              <button onClick={() => setLang('FR')} aria-pressed={lang === 'FR'} className={lang === 'FR' ? 'text-black font-medium' : 'hover:text-indigo transition-colors duration-200'} data-id="element-309">
                FR
              </button>
              <span className="text-[#E4E6EC]" data-id="element-310">|</span>
              <button onClick={() => setLang('EN')} aria-pressed={lang === 'EN'} className={lang === 'EN' ? 'text-black font-medium' : 'hover:text-indigo transition-colors duration-200'} data-id="element-311">
                EN
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}